/**
 * Hook that tracks browser online/offline state and pending IndexedDB action count.
 * Shows toasts on connectivity transitions.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import toast from 'react-hot-toast';
import { getPendingCount } from '../utils/offlineDb';

export function useOfflineStatus() {
  const { t } = useLanguage();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const prevOnline = useRef(navigator.onLine);

  const refreshCount = useCallback(async () => {
    try { setPendingSyncCount(await getPendingCount()); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (!prevOnline.current) toast.success(t('offline.backOnline'));
      prevOnline.current = true;
      refreshCount();
    };
    const handleOffline = () => {
      setIsOnline(false);
      if (prevOnline.current) toast.error(t('offline.youAreOffline'), { duration: 5000 });
      prevOnline.current = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshCount();

    const interval = setInterval(refreshCount, isOnline ? 10000 : 2000);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [isOnline, refreshCount, t]);

  return { isOnline, isOffline: !isOnline, pendingSyncCount, refreshCount };
}
